import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import api from '../../api/api'
import { useAuth } from '../../features/auth/AuthContext'
import DataTable from '../FileRepo/Components/DataTable'
import SearchBar from '../Search/SearchBar'
import FilterPopup from '../../pages/customFilter/FilterPopup'
import LowerSchoolAnalyticsModal from './LowerSchoolAnalyticsModal'
import './LowerSchoolSubmissions.css'
import BackButton from '../BackButton/BackButton'

/**
 * Status options for the filter dropdown. The ids are sent verbatim as the
 * `status` query param, so they mirror the values the API returns on each row.
 */
const statusFilterOptions = [
  { id: 'completed', name: 'Completed' },
  { id: 'in_progress', name: 'In Progress' },
  // { id: 'not_started', name: 'Not Started' },
]

/** "1 Jul 2026, 6:00 PM" — blank when the student hasn't reached that step. */
const formatDateTime = (value) =>
  value ? dayjs(value).format('D MMM YYYY, h:mm A') : '-'

/** Human label for an API status value ("in_progress" → "In Progress"). */
const formatStatus = (value) =>
  value
    ? value
        .split(/[_\s]+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '-'

const LowerSchoolSubmissions = () => {
  const { lesson_id } = useParams()
  const { token } = useAuth()

  /** `data` from /lessons/:id/lower-school/students/ — lesson, total_students, students[]. */
  const [submissions, setSubmissions] = useState(null)
  const [loading, setLoading] = useState(false)
  /** SearchBar owns the input and debounces into this. */
  const [search, setSearch] = useState('')
  /** Selected `status` filter value; empty string means "All". */
  const [status, setStatus] = useState('')
  const [isVisiblefillter, setIsVisiblefillter] = useState(false)
  /** Row whose analytics modal is open; null when closed. */
  const [analyticsStudent, setAnalyticsStudent] = useState(null)

  // Close the filter dropdown on any mousedown outside it. Bound only while the
  // dropdown is open, so the click that opens it can't immediately close it.
  const filterDropdownRef = useRef(null)
  useEffect(() => {
    if (!isVisiblefillter) return undefined

    const handleClickOutside = (event) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setIsVisiblefillter(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isVisiblefillter])

  // Refetches whenever the debounced search or the status filter changes -
  // the list comes from the API.
  useEffect(() => {
    if (!lesson_id || !token) return

    let isCancelled = false

    const getSubmissions = async () => {
      setLoading(true)
      try {
        const query = (search || '').trim()
        // Backend filters the list; omitted params return everyone.
        const params = {}
        if (query) params.search = query
        if (status) params.status = status

        const response = await api.get(
          `/lessons/${lesson_id}/lower-school/students/`,
          {
            params,
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Token ${token}`,
            },
          },
        )
        if (isCancelled) return
        if (response.status === 200) {
          setSubmissions(response.data?.data ?? null)
        } else {
          toast.error('Something went wrong.', { position: 'top-center' })
        }
      } catch (error) {
        if (isCancelled) return
        toast.error(
          error.response?.data?.message || 'Failed to load student submissions.',
          { position: 'top-center' },
        )
      } finally {
        // Guard the setState — the teacher can navigate away mid-request.
        if (!isCancelled) setLoading(false)
      }
    }

    getSubmissions()

    return () => {
      isCancelled = true
    }
  }, [lesson_id, token, search, status])

  const students = submissions?.students ?? []

  const columns = [
    {
      key: 'sr_no',
      title: 'Sr No.',
      minWidth : 25
    },
    {
      key: 'student_name',
      title: 'Student Name',
      sortable: true,
      render: (value, row) => (
        <div className="ls-submissions-student">
          {row?.student_profile_pic ? (
            <img
              className="ls-submissions-avatar"
              src={row.student_profile_pic}
              alt=""
            />
          ) : (
            <span className="ls-submissions-avatar ls-submissions-avatar-fallback">
              {(value || '?').charAt(0).toUpperCase()}
            </span>
          )}
          <span>{value}</span>
        </div>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      sortable: true,
      render: (value) => (
        <span
          className={`ls-submissions-status ls-submissions-status-${
            (value || 'unknown').toLowerCase().replace(/\s+/g, '-')
          }`}
        >
          {formatStatus(value)}
        </span>
      ),
    },
    {
      key: 'started_at',
      title: 'Started At',
      type: 'date',
      render: (value) => formatDateTime(value),
    },
    {
      key: 'completed_at',
      title: 'Completed At',
      type: 'date',
      render: (value) => formatDateTime(value),
    },
    {
      key: 'has_result',
      title: 'View Analytics',
      render: (value, row) =>
        value ? (
          <button
            type="button"
            className="ls-submissions-analytics"
            onClick={() => setAnalyticsStudent(row)}
          >
            View Analytics
          </button>
        ) : (
          <span className="ls-submissions-analytics-empty">-</span>
        ),
    },
  ]

  return (
    <div className="ls-submissions">
        <BackButton buttonName={'Back'}/>
      <div className="ls-submissions-head">
        <h2>{submissions?.lesson?.name || ''}</h2>
        {submissions?.lesson?.created_at && (
          <span>
            Create Date: {dayjs(submissions.lesson.created_at).format('D MMM YYYY')}
          </span>
        )}
      </div>

      {/* filterSort={false} hides SearchBar's Filter and Sort controls -
          the status filter below replaces them. */}
      <div className="ls-submissions-toolbar">
        <SearchBar
          search={search}
          setSearch={setSearch}
          filterSort={false}
          placeholder="Search Student Name"
        />

        <div className="LessonViewProgress-filder-tab-wrapper">
          <div
            className="AssignmentViewProgress-filter-popup-wrapper"
            ref={filterDropdownRef}
          >
            <div
              className="lessonviewprogress-filder-tab"
              onClick={() => setIsVisiblefillter((visible) => !visible)}
            >
              <span>
                <i className="fa-solid fa-sliders"></i>
              </span>
              <p>Filter</p>
            </div>
            <FilterPopup
              assignmentStatus={status}
              isVisiblefillter={isVisiblefillter}
              fillterstatus={statusFilterOptions}
              showApplyButton={false}
              // Picking a status applies it straight away: the effect above
              // refetches, and the dropdown has nothing left to show.
              handleAssignmentStatusChange={(event) => {
                setStatus(event.target.value)
                setIsVisiblefillter(false)
              }}
            />
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        dataSource={students}
        rowKey="student_id"
        loading={loading}
        // pagination={{ pageSize: 10 }}
        maxHeight="max-h-[60vh]"
        emptyHeadLabel="No Data Found"
        emptySecLabel="Students will appear here as they begin submitting the lesson."
      />

      <LowerSchoolAnalyticsModal
        open={Boolean(analyticsStudent)}
        onClose={() => setAnalyticsStudent(null)}
        lessonId={lesson_id}
        student={analyticsStudent}
      />
    </div>
  )
}

export default LowerSchoolSubmissions
